import React, {
  FocusEvent,
  FunctionComponent,
  KeyboardEvent,
  useEffect,
  useRef,
} from 'react';
import classNames from 'classnames';

import style from './PinField.module.scss';
import { KeyboardCode } from 'types/general';

interface Props {
  value: string[];
  length: number;
  hasError?: boolean;
  className?: string;
  onChange: (value: string[]) => void;
}

const PinField: FunctionComponent<Props> = ({
  value,
  length,
  hasError,
  className,
  onChange,
}) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleInputChange = (index: number, inputValue?: string) => {
    const values = inputValue?.split('');

    const newValue = [...value];

    if (values?.length) {
      for (let i = 0; i < values.length; i++) {
        const nextIndex = index + i;
        if (nextIndex >= length) continue;

        newValue[index + i] = values[i];
      }

      const nextInputIndex = index + values.length;
      focusInput(nextInputIndex);
    } else {
      newValue[index] = '';
    }

    onChange(newValue);
  };

  const handleKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    const prevInputIndex = index - 1;

    switch (event.key) {
      case KeyboardCode.Backspace: {
        if (!event.currentTarget.value) {
          focusInput(prevInputIndex);
        }
        break;
      }
      default:
        return;
    }
  };

  const focusInput = (inputIndex: number) => {
    inputRefs.current[inputIndex]?.focus();
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select();
  };

  const renderInputs = () => {
    const inputs: JSX.Element[] = [];

    for (let i = 0; i < length; i++) {
      inputs.push(
        <input
          key={i}
          ref={el => (inputRefs.current[i] = el)}
          value={value[i] ? '*' : ''}
          className={classNames(style.input, hasError && style.error)}
          onKeyDown={e => handleKeyDown(i, e)}
          onChange={e => handleInputChange(i, e.currentTarget.value)}
          onFocus={handleFocus}
        />,
      );
    }

    return inputs;
  };

  return (
    <div className={classNames(className, style.wrapper)}>{renderInputs()}</div>
  );
};

export default PinField;
